import { useCallback } from 'react'
import { groupBy } from 'es-toolkit'
import { ProductionRouteAsset } from '@/features/production-routes/api/ProductionRouteModel'
import { Artifacts, AssetEmissionRecord } from '@/api/EmissionReportModel'
import { EmissionTypeToReport } from './ProcessEmissionsSection'
import CombustionProcessEmissionReporting from './CombustionProcessEmissionReporting'
import Co2ProcessEmissionReporting from './Co2ProcessEmissionReporting'
import PfcProcessEmissionReporting from './PfcProcessEmissionReporting'

export type EmissionEditState = {
  productionRouteAssetId: number
  method: string
  emissionType: string
  emissionRecordId?: string
}

export default function ProcessEmissionsReporting({
  activityLevel,
  updateEmissionForAsset,
  isViewMode,
  emissionsToReport,
  createEmissionForAsset,
  removeEmissionForAsset,
  areCombustionAssetsComplete,
  areCo2AssetsComplete,
  arePfcAssetsComplete
}: {
  emissionsToReport: EmissionTypeToReport[]
  activityLevel: number
  updateEmissionForAsset: (emissionId: string, emissions: AssetEmissionRecord) => void
  isViewMode: boolean
  createEmissionForAsset: (emissions: AssetEmissionRecord) => void
  removeEmissionForAsset: (emissionId: string) => void
  areCombustionAssetsComplete: boolean
  areCo2AssetsComplete: boolean
  arePfcAssetsComplete: boolean
}) {
  const onEmissionRemove = useCallback(
    (emissionId: string) => {
      removeEmissionForAsset(emissionId)
    },
    [removeEmissionForAsset]
  )

  const onEmissionSubmit = useCallback(
    (
      payload: {
        emissionType: string
        method: string
        emission: number
        artifacts: Artifacts
        asset: ProductionRouteAsset
      },
      id?: string
    ) => {
      if (id) {
        updateEmissionForAsset(id, {
          ...payload,
          id: id
        })
      } else {
        createEmissionForAsset({
          ...payload,
          id: crypto.randomUUID()
        })
      }
    },
    [createEmissionForAsset, updateEmissionForAsset]
  )
  const emissionsByEmissionType = groupBy(emissionsToReport, asset => asset.emissionType)
  const emissionTypes = Object.keys(emissionsByEmissionType)
  return (
    <>
      {emissionTypes.map(emissionType => {
        const emissionTypeToReport = emissionsByEmissionType[emissionType]
        if (emissionType === 'COMBUSTION') {
          return (
            <CombustionProcessEmissionReporting
              key={emissionType}
              emissionsToReport={emissionTypeToReport}
              isViewMode={isViewMode}
              onEmissionSubmit={onEmissionSubmit}
              onEmissionRemove={onEmissionRemove}
              isComplete={areCombustionAssetsComplete}
            />
          )
        }

        if (emissionType === 'CO2') {
          return (
            <Co2ProcessEmissionReporting
              key={emissionType}
              activityLevel={activityLevel}
              emissionsToReport={emissionTypeToReport}
              isViewMode={isViewMode}
              onEmissionSubmit={onEmissionSubmit}
              onEmissionRemove={onEmissionRemove}
              isComplete={areCo2AssetsComplete}
            />
          )
        }

        if (emissionType === 'PFC') {
          return (
            <PfcProcessEmissionReporting
              key={emissionType}
              emissionsToReport={emissionTypeToReport}
              activityLevel={activityLevel}
              isViewMode={isViewMode}
              onEmissionSubmit={onEmissionSubmit}
              removeEmissionForAsset={onEmissionRemove}
              isComplete={arePfcAssetsComplete}
            />
          )
        }

        return null
      })}
    </>
  )
}
